import React, { useState, useEffect } from 'react';
import { formatCurrency, validatePhone, COUNTRY_CONFIGS } from '../utils/formatters';
import PasswordInput from './PasswordInput';

export default function CreateDriver({ user, editDriver, onBack, onSaved, operatorDetails, vendorOnly = false }) {
    const [activeTab, setActiveTab] = useState('Personal Info');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        countryCode: '+91',
        phoneNumber: '',
        altPhone: '',
        passportNumber: '',
        passportExpiry: '',
        nationality: '',
        civilId: '',
        civilIdExpiry: '',
        driverCode: '',

        onboardDate: '',
        status: user?.userType === 'vendor' ? 'Under Review' : 'Active',
        licenseNumber: '',
        licenseExpiry: '',
        probationPeriod: '',
        accommodationLocation: '',
        operatorId: '',
        vendorId: user?.userType === 'vendor' ? user._id : '',

        baseSalary: 0,
        fixedAllowances: 0,
        overtimeRate: 0,
        uniformCost: 0,
        accommodationCost: 0,
        otherDeduction: 0,

        leaves: [],
        requests: []
    });

    const [leaveFormData, setLeaveFormData] = useState({
        startDate: '',
        endDate: '',
        leaveType: ''
    });

    const [requestFormData, setRequestFormData] = useState({
        requestType: '',
        description: ''
    });

    const [documentsList, setDocumentsList] = useState([{ name: '', file: null }]);
    const [operators, setOperators] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [requestTypes, setRequestTypes] = useState([]);
    const [nationalities, setNationalities] = useState([]);
    const [accommodations, setAccommodations] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);

    const tabs = (vendorOnly && user?.userType !== 'vendor') ? ['Personal Info', 'Employment & HR'] : ['Personal Info', 'Employment & HR', 'Compensation', 'Leave Mgmt', 'Requests'];

    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : (formData.operatorId || ''));

    useEffect(() => {
        if (editDriver) {
            setFormData({
                ...editDriver,
                password: editDriver.password || '',
                passportExpiry: editDriver.passportExpiry ? editDriver.passportExpiry.split('T')[0] : '',
                civilIdExpiry: editDriver.civilIdExpiry ? editDriver.civilIdExpiry.split('T')[0] : '',
                onboardDate: editDriver.onboardDate ? editDriver.onboardDate.split('T')[0] : '',
                licenseExpiry: editDriver.licenseExpiry ? editDriver.licenseExpiry.split('T')[0] : '',
                requests: editDriver.requests || [],
                operatorId: typeof editDriver.operatorId === 'object' ? editDriver.operatorId?._id : (editDriver.operatorId || ''),
                vendorId: typeof editDriver.vendorId === 'object' ? editDriver.vendorId?._id : (editDriver.vendorId || '')
            });
        } else if (operatorDetails?.country) {
            const config = COUNTRY_CONFIGS[operatorDetails.country];
            if (config) {
                setFormData(prev => ({
                    ...prev,
                    countryCode: config.phoneCode
                }));
            }
        }
        if (user?.userType === 'admin') {
            fetch('/api/operators')
                .then(r => r.json())
                .then(data => setOperators(data))
                .catch(err => console.error('Error fetching operators:', err));
        }

        if (operatorId) {
            fetch(`/api/vendors/${operatorId}`)
                .then(r => r.json())
                .then(data => setVendors(data))
                .catch(err => console.error('Error fetching vendors:', err));
        }

        // Fetch Master Data for Leaves, Requests, Nationalities, Accommodations, Document Types
        fetch('/api/master-data')
            .then(r => r.json())
            .then(data => {
                setLeaveTypes(data.filter(d => d.category === 'leave-request-type' && d.active !== false));
                setRequestTypes(data.filter(d => d.category === 'other-request-type' && d.active !== false));
                setNationalities(data.filter(d => d.category === 'driver-nationalities' && d.active !== false));
                setAccommodations(data.filter(d => d.category === 'driver-accommodations' && d.active !== false));
                setDocumentTypes(data.filter(d => d.category === 'document-types' && d.active !== false));
            })
            .catch(err => console.error('Error fetching master data:', err));
    }, [editDriver, user, formData.operatorId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddDocument = () => {
        setDocumentsList(prev => [...prev, { name: '', file: null }]);
    };

    const handleDocumentChange = (index, field, value) => {
        const updated = [...documentsList];
        updated[index][field] = value;
        setDocumentsList(updated);
    };

    const handleRemoveDocument = (index) => {
        const updated = [...documentsList];
        updated.splice(index, 1);
        setDocumentsList(updated);
    };

    const handleAddLeave = () => {
        if (!leaveFormData.startDate || !leaveFormData.endDate || !leaveFormData.leaveType) return;
        setFormData(prev => ({
            ...prev,
            leaves: [...prev.leaves, { ...leaveFormData, status: 'Pending' }]
        }));
        setLeaveFormData({ startDate: '', endDate: '', leaveType: '' });
    };

    const handleAddRequest = () => {
        if (!requestFormData.requestType || !requestFormData.description) return;
        setFormData(prev => ({
            ...prev,
            requests: [...prev.requests, { ...requestFormData, status: 'Pending', date: new Date().toISOString() }]
        }));
        setRequestFormData({ requestType: '', description: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Require Driver ID before saving
        if (!formData.driverCode || !formData.driverCode.trim()) {
            setError('Driver ID / Code is required. Please enter it in the Personal Info tab.');
            setLoading(false);
            return;
        }

        const data = new FormData();
        // Append all text fields
        Object.keys(formData).forEach(key => {
            const val = formData[key];
            // Skip empty/system values — FormData would send null as the text "null"
            if (val === null || val === undefined) return;
            if (['_id', '__v', 'createdAt', 'updatedAt'].includes(key)) return;
            if (typeof val === 'object' && !Array.isArray(val) && !(val instanceof File)) {
                if (val._id && key !== 'operatorId' && key !== 'vendorId') data.append(key, val._id);
                return;
            }
            if (Array.isArray(formData[key])) {
                data.append(key, JSON.stringify(formData[key]));
            } else if (key !== 'operatorId' && key !== 'vendorId') {
                data.append(key, formData[key]);
            }
        });

        const documentNames = [];
        documentsList.forEach(doc => {
            if (doc.file) {
                data.append('documents', doc.file);
                documentNames.push(doc.name || 'Document');
            }
        });
        if (documentNames.length > 0) {
            data.append('documentNames', JSON.stringify(documentNames));
        }

        // Add operatorId and vendorId
        let opId = '';
        if (user?.operatorId) {
            opId = user.operatorId;
        } else if (formData.operatorId) {
            opId = typeof formData.operatorId === 'object' ? formData.operatorId._id : formData.operatorId;
        } else if (editDriver?.operatorId) {
            opId = typeof editDriver.operatorId === 'object' ? editDriver.operatorId._id : editDriver.operatorId;
        }

        if (opId) data.append('operatorId', opId);
        if (formData.vendorId) data.append('vendorId', formData.vendorId);

        if (formData.phoneNumber && !validatePhone(formData.phoneNumber, operatorDetails?.country)) {
            setError(`Primary Phone is invalid for ${operatorDetails?.country || 'the selected country'}.`);
            setLoading(false);
            return;
        }

        const url = editDriver
            ? `/api/drivers/${editDriver._id}`
            : `/api/drivers`;

        const method = editDriver ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                body: data
            });
            const result = await res.json();
            if (res.ok) {
                onSaved();
            } else {
                setError(result.message || 'Failed to save driver');
            }
        } catch (err) {
            setError('Server error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return dateStr.split('T')[0];
    };

    const Icon = ({ d, size = 16, strokeWidth = 2 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            <path d={d} />
        </svg>
    );

    const isTabDisabled = (tab) => {
        if (!editDriver && (tab === 'Leave Mgmt' || tab === 'Requests')) return true;
        return false;
    };

    // Currency prefix for salary inputs (e.g. KWD, AED, $) — pad input so text never overlaps it
    const driverCurSym = COUNTRY_CONFIGS[operatorDetails?.country]?.currencySymbol || '$';
    const driverCurPad = Math.max(32, 16 + String(driverCurSym).length * 11 + 14);

    return (
        <div className="create-driver-wrapper">
            <style>{`
                .create-driver-wrapper {
                    height: 100%;
                    width: 100%;
                    overflow: hidden;
                }
                .driver-ts-container {
                    display: flex;
                    height: 100%;
                    background: #f8fafc;
                    font-family: 'Inter', sans-serif;
                }
                .theme-dark .driver-ts-container { background: #0f172a; }

                /* Premium Sidebar Navigation */
                .ts-sidebar {
                    width: 280px;
                    background: white;
                    border-right: 1px solid #e2e8f0;
                    padding: 32px 16px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    flex-shrink: 0;
                    height: 100%;
                    overflow-y: auto;
                }
                .theme-dark .ts-sidebar { background: #1e293b; border-right-color: #334155; }

                .ts-nav-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 16px;
                    border-radius: 12px;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-size: 0.9375rem;
                    font-weight: 600;
                    cursor: pointer;
                    text-align: left;
                    transition: all 0.2s;
                }
                .ts-nav-item:hover:not(:disabled) { background: #f8fafc; color: #16539C; }
                .ts-nav-item.active { background: #eef2ff; color: #16539C; }
                .theme-dark .ts-nav-item.active { background: rgba(22, 83, 156, 0.1); color: #818cf8; }
                .ts-nav-item:disabled { opacity: 0.5; cursor: not-allowed; }

                /* Main Content Area */
                .ts-main {
                    flex: 1;
                    padding: 40px;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 32px;
                }

                .ts-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                }
                .ts-title-group h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .theme-dark .ts-title-group h1 { color: #f1f5f9; }
                .ts-title-group p { font-size: 14px; color: #64748b; margin: 4px 0 0; }

                .back-btn-premium {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .back-btn-premium:hover { background: #f8fafc; color: #1e293b; border-color: #cbd5e1; }

                /* Form Styling */
                .form-panel {
                    background: white;
                    border-radius: 20px;
                    padding: 32px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                    border: 1px solid #e2e8f0;
                    max-width: 1000px;
                }
                .theme-dark .form-panel { background: #1e293b; border-color: #334155; }

                .section-title { font-size: 1.125rem; font-weight: 700; margin-bottom: 24px; color: #1e293b; }
                .theme-dark .section-title { color: #f1f5f9; }

                .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
                .grid-3 { grid-template-columns: repeat(3, 1fr); }
                .span-2 { grid-column: span 2; }
                .span-3 { grid-column: span 3; }

                .form-group { display: flex; flex-direction: column; gap: 8px; }
                .form-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
                
                .form-input, .form-select, .form-textarea {
                    padding: 12px 16px;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 14px;
                    transition: all 0.2s;
                    background: #fff;
                    color: #1e293b;
                    outline: none;
                }
                .theme-dark .form-input, .theme-dark .form-select, .theme-dark .form-textarea {
                    background: #0f172a; border-color: #334155; color: white;
                }
                .form-input:focus, .form-select:focus { border-color: #16539C; box-shadow: 0 0 0 4px rgba(22, 83, 156, 0.1); }

                /* Specialized Components */
                .cost-summary {
                    grid-column: 1 / -1;
                    margin-top: 16px;
                    padding: 24px;
                    background: linear-gradient(135deg, #063A5C 0%, #312e81 100%);
                    border-radius: 16px;
                    color: white;
                    text-align: center;
                }
                .cost-label { font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.6); text-transform: uppercase; margin-bottom: 8px; }
                .cost-value { font-size: 32px; font-weight: 800; }

                .badge-pending { 
                    background: #fef3c7; color: #d97706; padding: 4px 10px; border-radius: 8px; font-size: 11px; font-weight: 700;
                }

                .footer-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 16px;
                    margin-top: 40px;
                    padding-top: 32px;
                    border-top: 1px solid #e2e8f0;
                }

                .btn-premium {
                    padding: 12px 28px;
                    border-radius: 12px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .btn-cancel { background: white; border: 1.5px solid #e2e8f0; color: #64748b; }
                .btn-cancel:hover { background: #f1f5f9; color: #1e293b; }
                
                .btn-save { background: #16539C; border: none; color: white; box-shadow: 0 4px 6px -1px rgba(22, 83, 156, 0.3); }
                .btn-save:hover { background: #11427D; transform: translateY(-1px); }

                .spinner { 
                    width: 18px; height: 18px; 
                    border: 2px solid rgba(255,255,255,0.3); 
                    border-top-color: white; 
                    border-radius: 50%; 
                    animation: spin 0.6s linear infinite; 
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* Tablet/Mobile fixes */
                @media (max-width: 1024px) {
                    .ts-sidebar { width: 220px; }
                }
            `}</style>

            <div className="driver-ts-container">
                {/* Internal Sidebar for Tabs */}
                <aside className="ts-sidebar" style={vendorOnly ? { borderRightColor: '#10b98120' } : {}}>
                    <div className="ts-nav-item active" style={{ marginBottom: 24, cursor: 'default', background: 'none', color: '#10b981', paddingLeft: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: vendorOnly ? '#10b981' : '#16539C' }}>{vendorOnly ? 'Vendor Driver' : 'Driver Profile'}</span>
                    </div>
                    {tabs.map(tab => (
                        <button
                            key={tab}
                            className={`ts-nav-item ${activeTab === tab ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab)}
                            disabled={isTabDisabled(tab)}
                            style={activeTab === tab && vendorOnly ? { background: '#ecfdf5', color: '#059669' } : {}}
                        >
                            {tab === 'Personal Info' && <Icon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />}
                            {tab === 'Employment & HR' && <Icon d="M21 10h-6V4l-4 4V4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-10z M16 6h2v2h-2z" />}
                            {tab === 'Compensation' && <Icon d="M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z M1 10h22" />}
                            {tab === 'Leave Mgmt' && <Icon d="M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z M16 2v4 M8 2v4 M3 10h18" />}
                            {tab === 'Requests' && <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />}
                            <span>{tab}</span>
                        </button>
                    ))}
                </aside>

                <main className="ts-main">
                    <header className="ts-header">
                        <div className="ts-title-group">
                            <h1>{editDriver ? `Edit ${vendorOnly ? 'Outsourced' : 'Direct'} Driver` : `Register ${vendorOnly ? 'Vendor Resource' : 'Direct Company Driver'}`}</h1>
                            <p>{editDriver ? `Update details for this ${vendorOnly ? 'vendor partner' : 'company fleet member'}` : `Initialize a new ${vendorOnly ? 'partner' : 'internal'} driver profile.`}</p>
                        </div>
                        <button className="back-btn-premium" onClick={onBack} style={vendorOnly ? { borderColor: '#10b98140' } : {}}>
                            <Icon d="M19 12H5M12 19l-7-7 7-7" size={16} strokeWidth={2.5} />
                            Back to List
                        </button>
                    </header>

                    {error && (
                        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '16px 24px', borderRadius: 16, border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, fontWeight: 500 }}>
                            <Icon d="M12 8v4M12 16h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" strokeWidth={2} />
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                        <div className="form-panel">
                            {activeTab === 'Personal Info' && (
                                <>
                                    <div className="section-title">Basic Information</div>
                                    <div className="form-grid" style={{ marginBottom: 32 }}>
                                        <div className="form-group">
                                            <label className="form-label" style={{ color: '#16539C' }}>Driver ID / Code *</label>
                                            <input className="form-input" type="text" name="driverCode" value={formData.driverCode} onChange={handleChange} required placeholder="e.g. D-001" style={{ borderColor: formData.driverCode ? '#e2e8f0' : '#fca5a5', fontWeight: 700 }} />
                                            <span style={{ fontSize: 11, color: '#64748b' }}>Must be unique — cannot match any existing Driver ID</span>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Full Name *</label>
                                            <input className="form-input" type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter driver's full name" />
                                        </div>
                                         <div className="form-group span-2">
                                             <label className="form-label">Email Address *</label>
                                             <input className="form-input" type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="example@email.com" />
                                         </div>
                                         <div className="form-group span-2">
                                             <label className="form-label">App Access Password *</label>
                                             <PasswordInput className="form-input" name="password" value={formData.password} onChange={handleChange} required placeholder="Set a secure password for the Driver App" />
                                         </div>
                                        <div className="form-group">
                                            <label className="form-label">Primary Phone *</label>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <select className="form-select" name="countryCode" value={formData.countryCode} onChange={handleChange} style={{ width: 100 }}>
                                                    {(operatorDetails?.country ? [COUNTRY_CONFIGS[operatorDetails.country]].filter(Boolean) : Object.values(COUNTRY_CONFIGS)).map(config => (
                                                        <option key={config.phoneCode} value={config.phoneCode}>
                                                            {config.phoneCode}
                                                        </option>
                                                    ))}
                                                </select>
                                                <input className="form-input" type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required placeholder="Number" style={{ flex: 1 }} />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Alternative Phone</label>
                                            <input className="form-input" type="text" name="altPhone" value={formData.altPhone} onChange={handleChange} placeholder="Emergency contact" />
                                        </div>
                                    </div>

                                    <div className="section-title">Identity & Documentation</div>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label">Nationality</label>
                                            <select className="form-select" name="nationality" value={formData.nationality} onChange={handleChange}>
                                                <option value="">-- Select Nationality --</option>
                                                {/* If editing and current value isn't in the list, show it as an option */}
                                                {formData.nationality && !nationalities.some(n => n.name === formData.nationality) && (
                                                    <option value={formData.nationality}>{formData.nationality}</option>
                                                )}
                                                {nationalities.length === 0 ? (
                                                    <option disabled value="">⚙ No options — add in Settings → Master Data</option>
                                                ) : (
                                                    nationalities.map(n => (
                                                        <option key={n._id} value={n.name}>{n.name}</option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            {/* Spacer for grid */}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Civil ID / National ID</label>
                                            <input className="form-input" type="text" name="civilId" value={formData.civilId} onChange={handleChange} placeholder="ID Number" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Civil ID Expiry</label>
                                            <input className="form-input" type="date" name="civilIdExpiry" value={formData.civilIdExpiry} onChange={handleChange} />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Passport Number</label>
                                            <input className="form-input" type="text" name="passportNumber" value={formData.passportNumber} onChange={handleChange} placeholder="Passport No." />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Passport Expiry</label>
                                            <input className="form-input" type="date" name="passportExpiry" value={formData.passportExpiry} onChange={handleChange} />
                                        </div>

                                        {/* License moved to Employment & HR */}

                                        {vendorOnly && user?.userType !== 'vendor' && (
                                            <div className="form-group span-2" style={{ marginTop: 16 }}>
                                                <label className="form-label" style={{ color: '#059669' }}>Assigned Vendor *</label>
                                                <select className="form-select" name="vendorId" value={formData.vendorId} onChange={handleChange} required style={{ borderColor: '#10b981' }}>
                                                    <option value="" disabled>-- Select Vendor --</option>
                                                    {vendors.map(v => (
                                                        <option key={v._id} value={v._id}>{v.vendorName}</option>
                                                    ))}
                                                </select>
                                                <p style={{ fontSize: 11, color: '#059669', marginTop: 4 }}>This driver will be linked to the selected vendor.</p>
                                            </div>
                                        )}

                                        {user?.userType === 'admin' && (
                                            <div className="form-group span-2" style={{ marginTop: 16 }}>
                                                <label className="form-label">Assigned Operator *</label>
                                                <select className="form-select" name="operatorId" value={formData.operatorId} onChange={handleChange} required>
                                                    <option value="">Select Operator</option>
                                                    {operators.map(op => (
                                                        <option key={op._id} value={op._id}>{op.companyName}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {activeTab === 'Employment & HR' && (
                                <>
                                    <div className="section-title">Professional Credentials</div>
                                    <div className="form-grid" style={{ marginBottom: 24 }}>
                                        <div className="form-group">
                                            <label className="form-label">License Number</label>
                                            <input className="form-input" type="text" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} placeholder="License No." />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">License Expiry</label>
                                            <input className="form-input" type="date" name="licenseExpiry" value={formData.licenseExpiry} onChange={handleChange} />
                                        </div>
                                    </div>

                                    <div className="section-title">Employment Specifics</div>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label">Date of Joining (Onboarding)</label>
                                            <input className="form-input" type="date" name="onboardDate" value={formData.onboardDate} onChange={handleChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Job Probation Status</label>
                                            <input className="form-input" type="text" name="probationPeriod" value={formData.probationPeriod} onChange={handleChange} placeholder="e.g. 90 Days" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Current Status</label>
                                            <select className="form-select" name="status" value={formData.status} onChange={handleChange}>
                                                <option value="Under Review">Under Review</option>
                                                <option value="Active">Active / On Duty</option>
                                                <option value="Inactive">Suspended</option>
                                                <option value="On Leave">Vacation / Leave</option>
                                                <option value="Resigned">Resigned</option>
                                                <option value="Terminated">Terminated</option>
                                            </select>
                                        </div>
                                        <div className="form-group span-2">
                                            <label className="form-label">Accommodation / Residential Location</label>
                                            <select className="form-select" name="accommodationLocation" value={formData.accommodationLocation} onChange={handleChange}>
                                                <option value="">-- Select Location --</option>
                                                {/* If editing and current value isn't in the list, show it as an option */}
                                                {formData.accommodationLocation && !accommodations.some(a => a.name === formData.accommodationLocation) && (
                                                    <option value={formData.accommodationLocation}>{formData.accommodationLocation}</option>
                                                )}
                                                {accommodations.length === 0 ? (
                                                    <option disabled value="">⚙ No options — add in Settings → Master Data</option>
                                                ) : (
                                                    accommodations.map(a => (
                                                        <option key={a._id} value={a.name}>{a.name}</option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div className="form-group span-2" style={{ background: '#f8fafc', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                                <label className="form-label" style={{ margin: 0 }}>Driver Documents</label>
                                                <button type="button" onClick={handleAddDocument} style={{ background: '#eef2ff', color: '#16539C', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <Icon d="M12 5v14M5 12h14" /> Add Document
                                                </button>
                                            </div>
                                            
                                            {editDriver?.documents?.length > 0 && (
                                                <div style={{ marginBottom: 20 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>Existing Documents</div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        {editDriver.documents.map((doc, idx) => (
                                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                                                <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" size={18} />
                                                                <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{doc.name || 'Document'}</span>
                                                                <a href={`/api/uploads/${doc.file}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', fontSize: 12, color: '#16539C', textDecoration: 'none', fontWeight: 600 }}>View</a>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                {documentsList.map((doc, index) => (
                                                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        <select
                                                            className="form-select"
                                                            value={doc.name}
                                                            onChange={e => handleDocumentChange(index, 'name', e.target.value)}
                                                            style={{ flex: 1, padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '14px', fontSize: '14px', fontFamily: "'Inter', sans-serif", fontWeight: 500, color: '#1e293b' }}
                                                        >
                                                            <option value="">Select Document Type</option>
                                                            {documentTypes.length === 0 ? (
                                                                <option disabled value="">⚙ No options — add in Settings → Master Data</option>
                                                            ) : (
                                                                documentTypes.map(dt => (
                                                                    <option key={dt._id} value={dt.name}>{dt.name}</option>
                                                                ))
                                                            )}
                                                            {doc.name && !documentTypes.some(d => d.name === doc.name) && (
                                                                <option value={doc.name}>{doc.name}</option>
                                                            )}
                                                        </select>
                                                        <input 
                                                            className="form-input" 
                                                            type="file" 
                                                            onChange={e => handleDocumentChange(index, 'file', e.target.files[0])} 
                                                            style={{ flex: 1 }}
                                                        />
                                                        {documentsList.length > 1 && (
                                                            <button type="button" onClick={() => handleRemoveDocument(index)} style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: 10, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Icon d="M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6" />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'Compensation' && (
                                <>
                                    <div className="section-title">Payroll & Allowances</div>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label">Basic Salary (Monthly)</label>
                                            <div style={{ position: 'relative' }}>
                                                <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontWeight: 700, fontSize: 14, color: '#1e293b', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                                                    {driverCurSym}
                                                </span>
                                                <input className="form-input" type="number" name="baseSalary" value={formData.baseSalary} onChange={handleChange} style={{ paddingLeft: driverCurPad }} />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Fixed Monthly Allowances</label>
                                            <div style={{ position: 'relative' }}>
                                                <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontWeight: 700, fontSize: 14, color: '#1e293b', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                                                    {driverCurSym}
                                                </span>
                                                <input className="form-input" type="number" name="fixedAllowances" value={formData.fixedAllowances} onChange={handleChange} style={{ paddingLeft: driverCurPad }} />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Overtime Percentage (%)</label>
                                            <input className="form-input" type="number" name="overtimeRate" value={formData.overtimeRate} onChange={handleChange} placeholder="e.g. 150" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Uniform Deductions/Cost</label>
                                            <input className="form-input" type="number" name="uniformCost" value={formData.uniformCost} onChange={handleChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Accommodation Cost (If Any)</label>
                                            <input className="form-input" type="number" name="accommodationCost" value={formData.accommodationCost} onChange={handleChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Other Deduction Amount</label>
                                            <div style={{ position: 'relative' }}>
                                                <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontWeight: 600, color: '#fca5a5' }}>
                                                    {COUNTRY_CONFIGS[operatorDetails?.country]?.currencySymbol ? '-' : '-'}
                                                </span>
                                                <input className="form-input" type="number" name="otherDeduction" value={formData.otherDeduction} onChange={handleChange} style={{ paddingLeft: 32 }} placeholder="Enter amount to deduct" />
                                            </div>
                                        </div>

                                        <div className="cost-summary">
                                            <div className="cost-label">Unified Monthly Expenditure</div>
                                            <div className="cost-value">
                                                {formatCurrency(
                                                    Number(formData.baseSalary) +
                                                    Number(formData.fixedAllowances) -
                                                    Number(formData.uniformCost || 0) -
                                                    Number(formData.accommodationCost || 0) -
                                                    Number(formData.otherDeduction || 0),
                                                    operatorDetails?.country
                                                )}
                                            </div>
                                            <p style={{ margin: '8px 0 0', fontSize: '12px', opacity: 0.6 }}>Net payout: (Basic + Fixed) - Deductions</p>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'Leave Mgmt' && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                                        <div>
                                            <div className="section-title" style={{ marginBottom: 4 }}>Leave Management</div>
                                            <div style={{ fontSize: 14, color: '#64748b' }}>Schedule and track driver time-off, sick leaves, and vacations</div>
                                        </div>
                                    </div>
                                    
                                    <div style={{ 
                                        background: 'linear-gradient(to right, #f8fafc, #f1f5f9)', 
                                        padding: '32px', 
                                        borderRadius: '24px', 
                                        border: '1px solid #e2e8f0', 
                                        marginBottom: '32px',
                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                                    }}>
                                        <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 20, fontSize: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{ background: '#e6f0fa', color: '#16539C', padding: 8, borderRadius: 10, display: 'flex' }}>
                                                <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={18} />
                                            </div>
                                            Schedule New Leave
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 16, alignItems: 'end' }}>
                                            <div className="form-group">
                                                <label className="form-label">Start Date</label>
                                                <input className="form-input" type="date" value={leaveFormData.startDate} onChange={e => setLeaveFormData({ ...leaveFormData, startDate: e.target.value })} style={{ background: 'white' }} />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">End Date</label>
                                                <input className="form-input" type="date" value={leaveFormData.endDate} onChange={e => setLeaveFormData({ ...leaveFormData, endDate: e.target.value })} style={{ background: 'white' }} />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Leave Type</label>
                                                <select className="form-select" value={leaveFormData.leaveType} onChange={e => setLeaveFormData({ ...leaveFormData, leaveType: e.target.value })} style={{ background: 'white' }}>
                                                    <option value="">Select Type</option>
                                                    <option value="Sick Leave">Sick Leave</option>
                                                    <option value="Personal Leave">Personal Leave</option>
                                                    <option value="Vacation">Vacation</option>
                                                    <option value="Unexcused Absence">Unexcused Absence</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                            <button type="button" onClick={handleAddLeave} style={{ 
                                                height: 46, padding: '0 28px', background: '#16539C', color: 'white', borderRadius: 12, border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(79,70,229,0.25)'
                                            }}>
                                                <Icon d="M12 5v14M5 12h14" size={18} strokeWidth={2.5} />
                                                Add Leave
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 16, fontSize: 16 }}>Leave History</div>
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {formData.leaves.length === 0 ? (
                                            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '60px 24px', background: '#f8fafc', borderRadius: 24, border: '2px dashed #e2e8f0' }}>
                                                <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={48} strokeWidth={1} style={{ opacity: 0.5, marginBottom: 16, display: 'inline-block' }} />
                                                <div style={{ fontSize: 16, fontWeight: 600, color: '#64748b' }}>No leave records found</div>
                                                <div style={{ fontSize: 14, marginTop: 4 }}>Scheduled leaves will appear here</div>
                                            </div>
                                        ) : (
                                            formData.leaves.map((l, i) => (
                                                <div key={i} style={{ 
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', 
                                                    background: 'white', borderRadius: 16, border: '1px solid #e2e8f0',
                                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', position: 'relative', overflow: 'hidden'
                                                }}>
                                                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: l.status === 'Approved' ? '#10b981' : l.status === 'Rejected' ? '#ef4444' : '#f59e0b' }}></div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                                                        <div style={{ 
                                                            width: 52, height: 52, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            background: l.leaveType === 'Sick Leave' ? '#fee2e2' : l.leaveType === 'Vacation' ? '#dcfce3' : '#e6f0fa',
                                                            color: l.leaveType === 'Sick Leave' ? '#ef4444' : l.leaveType === 'Vacation' ? '#10b981' : '#16539C'
                                                        }}>
                                                            <Icon d={l.leaveType === 'Sick Leave' ? "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" : "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"} size={24} />
                                                        </div>
                                                        <div>
                                                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>{l.leaveType || l.type}</div>
                                                            <div style={{ fontSize: 14, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                                                                <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" size={14} />
                                                                {formatDate(l.startDate)} <span style={{ color: '#cbd5e1' }}>→</span> {formatDate(l.endDate)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                        <select 
                                                            className="form-select" 
                                                            value={l.status || 'Pending'} 
                                                            onChange={(e) => {
                                                                const updated = [...formData.leaves];
                                                                updated[i].status = e.target.value;
                                                                setFormData({...formData, leaves: updated});
                                                            }}
                                                            style={{ 
                                                                padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                                                                background: l.status === 'Approved' ? '#ecfdf5' : l.status === 'Rejected' ? '#fef2f2' : '#fffbeb', 
                                                                color: l.status === 'Approved' ? '#059669' : l.status === 'Rejected' ? '#ef4444' : '#d97706', 
                                                                borderColor: l.status === 'Approved' ? '#a7f3d0' : l.status === 'Rejected' ? '#fecaca' : '#fde68a', 
                                                                cursor: 'pointer', outline: 'none'
                                                            }}
                                                        >
                                                            <option value="Pending">Pending Review</option>
                                                            <option value="Approved">Approved</option>
                                                            <option value="Rejected">Rejected</option>
                                                        </select>
                                                        
                                                        <button type="button" onClick={() => {
                                                            const updated = [...formData.leaves];
                                                            updated.splice(i, 1);
                                                            setFormData({...formData, leaves: updated});
                                                        }} style={{ 
                                                            background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', cursor: 'pointer',
                                                            padding: 10, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            transition: 'all 0.2s'
                                                        }}>
                                                            <Icon d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" size={18} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </>
                            )}

                            {activeTab === 'Requests' && (
                                <>
                                    <div className="section-title">Miscellaneous Requests</div>
                                    <div className="form-grid" style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '32px' }}>
                                        <div className="form-group">
                                            <label className="form-label">Service Request Type</label>
                                            <select className="form-select" value={requestFormData.requestType} onChange={e => setRequestFormData({ ...requestFormData, requestType: e.target.value })}>
                                                <option value="">Specify requirement...</option>
                                                {requestTypes.map(t => (
                                                    <option key={t._id} value={t.name}>{t.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group span-2" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 12 }}>
                                            <div style={{ flex: 1 }}>
                                                <label className="form-label">Elaborate Description</label>
                                                <input className="form-input" type="text" value={requestFormData.description} onChange={e => setRequestFormData({ ...requestFormData, description: e.target.value })} placeholder="State details here..." />
                                            </div>
                                            <button type="button" className="btn-save btn-premium" style={{ width: 44, height: 44, padding: 0, justifyContent: 'center' }} onClick={handleAddRequest}>
                                                <Icon d="M12 5v14M5 12h14" size={20} strokeWidth={3} />
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <th style={{ padding: '16px', textAlign: 'left', fontSize: 12, color: '#64748b', textTransform: 'uppercase' }}>Request Category</th>
                                                    <th style={{ padding: '16px', textAlign: 'left', fontSize: 12, color: '#64748b', textTransform: 'uppercase' }}>Explanation</th>
                                                    <th style={{ padding: '16px', textAlign: 'left', fontSize: 12, color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.requests.length === 0 ? (
                                                    <tr><td colSpan="3" style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>No requests submitted.</td></tr>
                                                ) : (
                                                    formData.requests.map((r, i) => (
                                                        <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                                                            <td style={{ padding: '16px', fontWeight: 600, color: '#1e293b' }}>{r.requestType || r.type}</td>
                                                            <td style={{ padding: '16px' }}>{r.description}</td>
                                                            <td style={{ padding: '16px' }}>
                                                                <select 
                                                                    className="form-select" 
                                                                    value={r.status || 'Pending'} 
                                                                    onChange={(e) => {
                                                                        const updated = [...formData.requests];
                                                                        updated[i].status = e.target.value;
                                                                        setFormData({...formData, requests: updated});
                                                                    }}
                                                                    style={{ 
                                                                        padding: '6px 10px', 
                                                                        borderRadius: 8, 
                                                                        fontSize: 12, 
                                                                        height: 'auto', 
                                                                        fontWeight: 700, 
                                                                        background: r.status === 'Approved' ? '#ecfdf5' : r.status === 'Rejected' ? '#fef2f2' : '#e6f0fa', 
                                                                        color: r.status === 'Approved' ? '#059669' : r.status === 'Rejected' ? '#ef4444' : '#11427D', 
                                                                        borderColor: 'transparent',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    <option value="Pending">Pending</option>
                                                                    <option value="Approved">Approved</option>
                                                                    <option value="Rejected">Rejected</option>
                                                                </select>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            <div className="footer-actions">
                                <button type="button" className="btn-premium btn-cancel" onClick={onBack}>Discard Changes</button>
                                <button type="submit" className="btn-premium btn-save" disabled={loading} style={vendorOnly ? { background: '#10b981', boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)' } : {}}>
                                    {loading ? <div className="spinner"></div> : <Icon d="M5 13l4 4L19 7" size={18} strokeWidth={3} />}
                                    {loading ? 'Processing...' : editDriver ? 'Update Driver Profile' : vendorOnly ? 'Confirm & Register Resource' : 'Confirm & Create Profile'}
                                </button>
                            </div>
                        </div>
                    </form>
                </main>
            </div>
        </div>
    );
}
